import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addRfpQuestionComment,
  addRfpQuestionReviewer,
  fetchRfpQuestionDiscussion,
  removeRfpQuestionReviewer,
} from "../services/rfpsService";
import type { RfpQuestionDiscussionResponse } from "../types/rfp";

type UseRfpQuestionDiscussionArgs = {
  readonly rfpId: string | null;
  readonly questionId: string | null;
  readonly isOpen: boolean;
};

export type UseRfpQuestionDiscussionResult = {
  readonly discussion: RfpQuestionDiscussionResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly isAddingReviewer: boolean;
  readonly isRemovingReviewer: boolean;
  readonly isPostingComment: boolean;
  readonly loadDiscussion: () => Promise<void>;
  readonly addReviewer: (userId: string) => Promise<boolean>;
  readonly removeReviewer: (userId: string) => Promise<boolean>;
  readonly addComment: (commentText: string) => Promise<boolean>;
};

export const useRfpQuestionDiscussion = ({
  rfpId,
  questionId,
  isOpen,
}: UseRfpQuestionDiscussionArgs): UseRfpQuestionDiscussionResult => {
  const [discussion, setDiscussion] = useState<RfpQuestionDiscussionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingReviewer, setIsAddingReviewer] = useState(false);
  const [isRemovingReviewer, setIsRemovingReviewer] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const latestRequestIdRef = useRef(0);

  const normalizedRfpId = rfpId?.trim() ?? "";
  const normalizedQuestionId = questionId?.trim() ?? "";
  const contextKey = useMemo(
    () => `${normalizedRfpId}:${normalizedQuestionId}`,
    [normalizedQuestionId, normalizedRfpId],
  );
  const activeContextKeyRef = useRef(contextKey);

  useEffect(() => {
    activeContextKeyRef.current = contextKey;
  }, [contextKey]);

  useEffect(() => {
    if (!isOpen || !normalizedRfpId || !normalizedQuestionId) {
      setDiscussion(null);
      setLoading(false);
      setError(null);
      setIsAddingReviewer(false);
      setIsRemovingReviewer(false);
      setIsPostingComment(false);
      return;
    }

    setDiscussion(null);
    setError(null);
  }, [isOpen, normalizedQuestionId, normalizedRfpId]);

  const loadDiscussion = useCallback(async () => {
    if (!normalizedRfpId || !normalizedQuestionId) {
      setDiscussion(null);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const requestContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;

    setLoading(true);
    setError(null);
    try {
      const response = await fetchRfpQuestionDiscussion(normalizedRfpId, normalizedQuestionId);
      if (
        latestRequestIdRef.current !== requestId ||
        activeContextKeyRef.current !== requestContextKey
      ) {
        return;
      }
      setDiscussion(response);
    } catch (e) {
      if (
        latestRequestIdRef.current !== requestId ||
        activeContextKeyRef.current !== requestContextKey
      ) {
        return;
      }
      const message = e instanceof Error ? e.message : "Failed to load discussion";
      setError(message);
      setDiscussion(null);
    } finally {
      if (
        latestRequestIdRef.current === requestId &&
        activeContextKeyRef.current === requestContextKey
      ) {
        setLoading(false);
      }
    }
  }, [normalizedQuestionId, normalizedRfpId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadDiscussion();
  }, [isOpen, loadDiscussion]);

  const addReviewer = useCallback(
    async (userId: string): Promise<boolean> => {
      const normalizedUserId = userId.trim();
      const requestContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;
      if (
        !isOpen ||
        !normalizedRfpId ||
        !normalizedQuestionId ||
        !normalizedUserId ||
        isAddingReviewer
      ) {
        return false;
      }

      setIsAddingReviewer(true);
      setError(null);
      try {
        await addRfpQuestionReviewer(normalizedRfpId, normalizedQuestionId, { user_id: normalizedUserId });
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        void loadDiscussion();
        return true;
      } catch (e) {
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        const message = e instanceof Error ? e.message : "Failed to add reviewer";
        setError(message);
        return false;
      } finally {
        if (activeContextKeyRef.current === requestContextKey) {
          setIsAddingReviewer(false);
        }
      }
    },
    [isAddingReviewer, isOpen, loadDiscussion, normalizedQuestionId, normalizedRfpId],
  );

  const addComment = useCallback(
    async (commentText: string): Promise<boolean> => {
      const normalizedComment = commentText.trim();
      const requestContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;
      if (
        !isOpen ||
        !normalizedRfpId ||
        !normalizedQuestionId ||
        !normalizedComment ||
        isPostingComment
      ) {
        return false;
      }

      setIsPostingComment(true);
      setError(null);
      try {
        await addRfpQuestionComment(normalizedRfpId, normalizedQuestionId, { comment_text: normalizedComment });
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        await loadDiscussion();
        return true;
      } catch (e) {
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        const message = e instanceof Error ? e.message : "Failed to add comment";
        setError(message);
        return false;
      } finally {
        if (activeContextKeyRef.current === requestContextKey) {
          setIsPostingComment(false);
        }
      }
    },
    [isOpen, isPostingComment, loadDiscussion, normalizedQuestionId, normalizedRfpId],
  );

  const removeReviewer = useCallback(
    async (userId: string): Promise<boolean> => {
      const normalizedUserId = userId.trim();
      const requestContextKey = `${normalizedRfpId}:${normalizedQuestionId}`;
      if (
        !isOpen ||
        !normalizedRfpId ||
        !normalizedQuestionId ||
        !normalizedUserId ||
        isRemovingReviewer
      ) {
        return false;
      }

      setIsRemovingReviewer(true);
      setError(null);
      try {
        await removeRfpQuestionReviewer(normalizedRfpId, normalizedQuestionId, normalizedUserId);
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        void loadDiscussion();
        return true;
      } catch (e) {
        if (activeContextKeyRef.current !== requestContextKey) {
          return false;
        }
        const message = e instanceof Error ? e.message : "Failed to remove reviewer";
        setError(message);
        return false;
      } finally {
        if (activeContextKeyRef.current === requestContextKey) {
          setIsRemovingReviewer(false);
        }
      }
    },
    [isOpen, isRemovingReviewer, loadDiscussion, normalizedQuestionId, normalizedRfpId],
  );

  return {
    discussion,
    loading,
    error,
    isAddingReviewer,
    isRemovingReviewer,
    isPostingComment,
    loadDiscussion,
    addReviewer,
    removeReviewer,
    addComment,
  };
};
